import { CloudUploadOutlined, Close as CloseIcon } from '@mui/icons-material';
import { Box, CircularProgress, FormControl, FormLabel, IconButton, Typography } from '@mui/material';
import React, { useContext, useRef } from 'react';

import CurrentUserContext from '@/Contexts/CurrentUser';
import PermissionContext from '@/Contexts/Permissions';
import { PermissionActionEnum, PermissionEntityEnum } from '@/graphql/generated/graphql';
import useDirectUpload from '@/hooks/useDirectUpload';
import { MAX_FILE_SIZE_IN_MEGA_BYTES } from '@/utils/constants';
import { displayNotification } from '@/utils/notifications';

interface CompanyLogoProps {
  onImageChange: (id: string) => void;
  pendingLogoPreviewUrl: string | null;
  setPendingLogoPreviewUrl: React.Dispatch<React.SetStateAction<string | null>>;
  pendingLogoSignedId: string | null;
  setPendingLogoSignedId: React.Dispatch<React.SetStateAction<string | null>>;
}

const CompanyLogo: React.FC<CompanyLogoProps> = ({
  onImageChange,
  pendingLogoPreviewUrl,
  setPendingLogoPreviewUrl,
  pendingLogoSignedId,
  setPendingLogoSignedId,
}) => {
  const { currentCompany } = useContext(CurrentUserContext);
  const logoFileInputRef = useRef<HTMLInputElement>(null);
  const logoClearRef = useRef<HTMLElement>(null);
  const { isUploading: isLogoUploading, uploadFile: uploadLogoFile } = useDirectUpload();

  const { canPerformAction } = useContext(PermissionContext);
  const { Manage } = PermissionActionEnum;
  const { CompanySettings } = PermissionEntityEnum;
  const hasPermission = canPerformAction(Manage, CompanySettings);

  const companyLogoUrl = pendingLogoPreviewUrl ?? currentCompany?.imageUrl ?? null;

  const clearPendingCompanyLogo = () => {
    if (pendingLogoPreviewUrl) {
      URL.revokeObjectURL(pendingLogoPreviewUrl);
    }
    setPendingLogoSignedId(null);
    setPendingLogoPreviewUrl(null);
  };

  const handleCompanyLogoFile = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      displayNotification({ type: 'error', text: 'Please choose an image file (PNG, JPG, WebP, etc.).' });
      return;
    }
    if (file.size / 1000000 > MAX_FILE_SIZE_IN_MEGA_BYTES) {
      displayNotification({
        type: 'error',
        text: `Max allowed file size is ${MAX_FILE_SIZE_IN_MEGA_BYTES} MB.`,
      });
      return;
    }

    setPendingLogoPreviewUrl((previousPreviewUrl) => {
      if (previousPreviewUrl) URL.revokeObjectURL(previousPreviewUrl);
      return URL.createObjectURL(file);
    });
    setPendingLogoSignedId(null);

    const result = await uploadLogoFile(file, 'company-logos');
    if (result?.signedBlobId) {
      setPendingLogoSignedId(result.signedBlobId);
      onImageChange(result.signedBlobId);
    } else {
      displayNotification({ type: 'error', text: 'Logo upload failed. Please try again.' });
      setPendingLogoPreviewUrl((previousPreviewUrl) => {
        if (previousPreviewUrl) URL.revokeObjectURL(previousPreviewUrl);
        return null;
      });
    }
  };

  return (
    <FormControl fullWidth>
      <FormLabel sx={{ fontSize: 12, mb: 0.5 }}>Company logo (optional)</FormLabel>
      <Box
        role="button"
        aria-label="Upload company logo"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!isLogoUploading) logoFileInputRef.current?.click();
          }
        }}
        onClick={() => {
          if (!isLogoUploading) logoFileInputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!hasPermission) return;
          void handleCompanyLogoFile(e.dataTransfer.files[0]);
        }}
        sx={{
          position: 'relative',
          minHeight: 140,
          borderRadius: 2,
          border: '2px dashed',
          borderColor: 'divider',
          bgcolor: 'action.hover',
          cursor: isLogoUploading ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          transition: 'border-color 0.2s ease, background-color 0.2s ease',
          '&:hover': {
            borderColor: isLogoUploading ? 'divider' : 'primary.main',
            bgcolor: isLogoUploading ? 'action.hover' : 'action.selected',
          },
        }}
      >
        <input
          ref={logoFileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            void handleCompanyLogoFile(e.target.files?.[0]);
            e.target.value = '';
          }}
          disabled={isLogoUploading || !hasPermission}
        />
        {companyLogoUrl ? (
          <>
            <Box
              component="img"
              alt="Company logo preview"
              src={companyLogoUrl}
              sx={{
                maxWidth: '100%',
                maxHeight: 120,
                objectFit: 'contain',
                p: 1,
                opacity: isLogoUploading ? 0.45 : 1,
                transition: 'opacity 0.2s ease',
              }}
            />
            {isLogoUploading && (
              <>
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'action.disabledBackground',
                    borderRadius: 1,
                  }}
                  aria-hidden
                />
                <CircularProgress size={40} sx={{ position: 'absolute' }} aria-label="Uploading logo" />
              </>
            )}
            {pendingLogoSignedId && !isLogoUploading && (
              <span
                ref={logoClearRef}
                onClick={(e) => {
                  e.stopPropagation();

                  clearPendingCompanyLogo();
                }}
              >
                <IconButton
                  type="button"
                  size="small"
                  aria-label="Remove selected logo"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'background.paper' },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    clearPendingCompanyLogo();
                  }}
                  disabled={!hasPermission}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </span>
            )}
          </>
        ) : isLogoUploading ? (
          <CircularProgress size={40} aria-label="Uploading logo" />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              py: 2,
              px: 2,
              textAlign: 'center',
            }}
          >
            <CloudUploadOutlined color="action" sx={{ fontSize: 40, opacity: 0.85 }} />
            <Typography variant="body2" color="text.secondary">
              Drag and drop an image here, or click to browse
            </Typography>
            <Typography variant="caption" color="text.disabled">
              PNG, JPG, WebP up to {MAX_FILE_SIZE_IN_MEGA_BYTES} MB
            </Typography>
          </Box>
        )}
      </Box>
    </FormControl>
  );
};

export default CompanyLogo;
